// frontend/src/components/homepage/FaqLive.js
//
// "שאלות נפוצות" - the single-open accordion, a literal port of the prototype's
// `.faq` block (P.10 of the homepage port).
//
// CONTENT: components/homepage/faqData.js, unchanged - the same array the FAQPage
// JSON-LD is built from, which is what keeps the structured data and the
// on-screen text identical. `**bold**` inside an answer is rendered by the
// shared inline parser (renderBold); "\n" splits an answer into paragraphs.
//
// EVERY ANSWER STAYS IN THE DOM while collapsed. The panel collapses by
// animating `grid-template-rows: 0fr -> 1fr` (pricingFaqBlog.css), so the text
// is always mounted and the prerendered snapshot carries every answer for
// crawlers. A collapsed panel is `inert`, which takes its links out of the tab
// order and its text out of the accessibility tree without a display change -
// a screen reader hears the shown questions and one answer, as sighted readers
// see it.
//
// THE FIRST EIGHT, THEN "SHOW ALL" (owner, 2026-10). A list longer than
// VISIBLE_COUNT shows its first eight questions and one button that reveals the
// rest. The rest are still RENDERED - every question and every answer is in the
// DOM and in the prerendered HTML - and are hidden by a CLASS
// (`hl-faq-collapsed`, display:none in pricingFaqBlog.css), never an inline
// style: scripts/prerender.js flags inline `opacity: 0` text in an anchored
// section, and the snapshot is taken in exactly this collapsed state, so the
// server HTML and the first client render agree (no hydration mismatch, no
// `__PRERENDER__` branch). The FAQPage JSON-LD is built from faqData.js, not
// from this DOM, so it always lists every question. Expanding is one way: the
// button goes away and focus moves to the first question it revealed, so a
// keyboard user lands where the new content starts instead of on <body>. The
// label is one entry per language in SHOW_ALL_LABEL; a language without an
// approved label would render the whole list, uncollapsed.
//
// The question is a heading wrapping a button (the WAI-ARIA accordion shape):
// the page has exactly one <h1> in the hero, so these are <h3> under the
// section's <h2>. `aria-expanded` lives on the button, `aria-controls` points
// at the panel, and the panel names its heading back.

import React, { useState, useCallback, useEffect, useRef } from "react";
import SectionShell from "./SectionShell";
import useReveal from "../../motion/useReveal";
import { faqItems } from "../homepage/faqData";
import { renderBold } from "../shared/inlineMarkup";
import "../../homepage/pricingFaqBlog.css";

/** The section heading - one constant per language. */
export const HEADING = {
  he: {
    eyebrow: "שאלות נפוצות",
    title: "כל מה שחשוב לדעת",
    sub: "התשובות לשאלות הנפוצות ביותר על לידיפיי",
  },
  en: {
    eyebrow: "FAQ",
    title: "Everything You Need to Know",
    sub: "Answers to the most common questions about Leadify",
  },
};

/** Which item opens first. The prototype opens the first one. */
export const DEFAULT_OPEN_INDEX = 0;

/** How many questions show before the "show all" button. */
export const VISIBLE_COUNT = 8;

/**
 * The "show all" label per language, given the TOTAL number of questions
 * (computed from the list, never typed). A language with no entry has no
 * approved wording, so its list is never collapsed.
 */
export const SHOW_ALL_LABEL = {
  he: (total) => `הצגת כל השאלות (${total})`,
  en: (total) => `Show all questions (${total})`,
};

/** The list's id - what the "show all" button's `aria-controls` names. */
export const FAQ_LIST_ID = "hl-faq-list";

/**
 * One accordion item.
 *
 * `open` is owned by the list (single-open), so the item is a pure function of
 * its props and the list can flip two items in one state change. `collapsed`
 * hides the whole item behind the "show all" button; `buttonRef` is set only
 * on the first collapsed item, which takes focus when the list expands.
 */
function FaqItem({ index, question, answer, open, collapsed, buttonRef, onToggle }) {
  const questionId = `hl-faq-q-${index}`;
  const panelId = `hl-faq-a-${index}`;
  let className = open ? "hl-faq-item hl-faq-open" : "hl-faq-item";
  if (collapsed) className += " hl-faq-collapsed";
  return (
    <div className={className}>
      <h3 className="hl-faq-h">
        <button
          ref={buttonRef}
          type="button"
          className="hl-faq-q"
          id={questionId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span>{question}</span>
          <span className="hl-faq-pm" aria-hidden="true" />
        </button>
      </h3>
      <div
        className="hl-faq-a"
        id={panelId}
        role="region"
        aria-labelledby={questionId}
        inert={!open}
      >
        <div>
          {answer.split("\n").map((line, li) => (
            <p key={li}>{renderBold(line, `faq${index}-${li}`)}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {Array<{q: object, a: object}>} [props.items] the questions to render,
 *   same shape as `faqItems`. Defaults to the homepage's own list, so
 *   `Homepage.js` passes nothing and renders what it always did.
 */
export default function FaqLive({ isHebrew = true, items = faqItems }) {
  const t = isHebrew ? HEADING.he : HEADING.en;
  const lang = isHebrew ? "he" : "en";
  const head = useReveal();
  const list = useReveal();
  const [openIndex, setOpenIndex] = useState(DEFAULT_OPEN_INDEX);
  const [showAll, setShowAll] = useState(false);
  const firstRevealedRef = useRef(null);
  // Set by the button's click, read once by the effect below: focus moves only
  // in answer to the user expanding the list, never on a re-render.
  const focusPending = useRef(false);

  const showAllLabel = SHOW_ALL_LABEL[lang];
  const collapsible = Boolean(showAllLabel) && items.length > VISIBLE_COUNT;
  const collapsed = collapsible && !showAll;

  // Clicking the open item closes it; clicking another opens that one and
  // closes the open one - the prototype's exact rule.
  const toggle = useCallback((index) => {
    setOpenIndex((current) => (current === index ? -1 : index));
  }, []);

  const expand = useCallback(() => {
    focusPending.current = true;
    setShowAll(true);
  }, []);

  // After the revealed items are displayed (the class is gone), move focus to
  // the first of them - the button that had focus no longer exists.
  useEffect(() => {
    if (!showAll || !focusPending.current) return;
    focusPending.current = false;
    if (firstRevealedRef.current) firstRevealedRef.current.focus();
  }, [showAll]);

  return (
    <SectionShell id="faq" scene="faq" mesh="disperse" meshAlpha=".2" className="hl-faq">
      <div
        ref={head.ref}
        className={head.className ? `hl-head hl-head-center ${head.className}` : "hl-head hl-head-center"}
      >
        <div className="hl-eyebrow">{t.eyebrow}</div>
        <h2 className="hl-h2 hl-h2-center">{t.title}</h2>
        <p className="hl-sub hl-sub-center">{t.sub}</p>
      </div>

      <div
        ref={list.ref}
        id={FAQ_LIST_ID}
        className={list.className ? `hl-faq-list ${list.className}` : "hl-faq-list"}
      >
        {items.map((item, index) => (
          <FaqItem
            key={index}
            index={index}
            question={item.q[lang]}
            answer={item.a[lang]}
            open={openIndex === index}
            collapsed={collapsed && index >= VISIBLE_COUNT}
            buttonRef={collapsible && index === VISIBLE_COUNT ? firstRevealedRef : undefined}
            onToggle={() => toggle(index)}
          />
        ))}
      </div>

      {collapsed ? (
        <div className="hl-faq-more-wrap">
          <button
            type="button"
            className="hl-faq-more"
            aria-expanded="false"
            aria-controls={FAQ_LIST_ID}
            onClick={expand}
          >
            {showAllLabel(items.length)}
          </button>
        </div>
      ) : null}
    </SectionShell>
  );
}
